import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Package, AlertTriangle, DollarSign,
  BarChart3, PieChart, RefreshCw, ArrowRight, ShoppingCart
} from 'lucide-react';

const WMSDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/wms/dashboard-stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching WMS stats:', error);
      toast.error('Eroare la incarcarea statisticilor');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12 text-slate-500">
        Nu s-au putut incarca statisticile
      </div>
    );
  }

  // Calculate max for bar chart
  const maxConsumed = Math.max(...(stats.top_consumed?.map(c => c.quantity) || [1]));

  return (
    <div className="space-y-6">
      {/* Top Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Valoare Totala Stoc</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.total_value)}</p>
              </div>
              <DollarSign className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Total Articole</p>
                <p className="text-2xl font-bold">{stats.total_articles}</p>
              </div>
              <Package className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className={`${stats.low_stock_items?.length > 0 ? 'bg-gradient-to-br from-orange-500 to-orange-600' : 'bg-gradient-to-br from-slate-500 to-slate-600'} text-white`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Stoc Scazut</p>
                <p className="text-2xl font-bold">{stats.low_stock_items?.length || 0}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-orange-200" />
            </div>
          </CardContent>
        </Card>

        <Card className={`${stats.critical_stock_items?.length > 0 ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-slate-500 to-slate-600'} text-white`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Stoc Critic (0)</p>
                <p className="text-2xl font-bold">{stats.critical_stock_items?.length || 0}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Stock by Category */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-600" />
              Stoc pe Categorii
            </CardTitle>
            <CardDescription>Distributia valorica a stocului</CardDescription>
          </CardHeader>
          <CardContent>
            {stats.stock_by_category?.length === 0 ? (
              <p className="text-slate-500 text-center py-4">Nicio categorie</p>
            ) : (
              <div className="space-y-3">
                {stats.stock_by_category?.slice(0, 8).map((cat, index) => {
                  const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-orange-500', 'bg-cyan-500'];
                  const percentage = stats.total_value > 0 ? (cat.value / stats.total_value * 100) : 0;
                  
                  return (
                    <div key={cat.category} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{cat.category}</span>
                        <span className="text-slate-500">{formatCurrency(cat.value)} ({cat.count} buc)</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${colors[index % colors.length]} rounded-full transition-all`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Consumed Items */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              Top 10 Consumate (30 zile)
            </CardTitle>
            <CardDescription>Articolele cu cel mai mare consum</CardDescription>
          </CardHeader>
          <CardContent>
            {stats.top_consumed?.length === 0 ? (
              <p className="text-slate-500 text-center py-4">Nicio miscare in ultimele 30 zile</p>
            ) : (
              <div className="space-y-2">
                {stats.top_consumed?.map((item, index) => (
                  <div key={item.article_id} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0 ? 'bg-red-100 text-red-600' :
                      index === 1 ? 'bg-orange-100 text-orange-600' :
                      index === 2 ? 'bg-yellow-100 text-yellow-600' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-red-500 rounded-full"
                          style={{ width: `${(item.quantity / maxConsumed) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-12 text-right">{item.quantity}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            Tendinta Lunara
          </CardTitle>
          <CardDescription>Intrari vs. Iesiri din stoc</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4 h-48 pt-4">
            {stats.monthly_trend?.map((month, index) => {
              const maxVal = Math.max(
                ...stats.monthly_trend.map(m => Math.max(m.in, m.out)),
                1
              );
              const inHeight = (month.in / maxVal) * 100;
              const outHeight = (month.out / maxVal) * 100;
              
              return (
                <div key={index} className="flex-1 flex flex-col items-center gap-2">
                  <div className="flex-1 w-full flex items-end justify-center gap-1">
                    <div 
                      className="w-5 bg-green-500 rounded-t transition-all"
                      style={{ height: `${Math.max(inHeight, 4)}%` }}
                      title={`Intrari: ${month.in}`}
                    />
                    <div 
                      className="w-5 bg-red-500 rounded-t transition-all"
                      style={{ height: `${Math.max(outHeight, 4)}%` }}
                      title={`Iesiri: ${month.out}`}
                    />
                  </div>
                  <span className="text-xs text-slate-500">{month.month}</span>
                </div>
              );
            })}
          </div>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded" />
              <span className="text-slate-600">Intrari</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded" />
              <span className="text-slate-600">Iesiri</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alerts Section */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Low Stock Alert */}
        <Card className={stats.low_stock_items?.length > 0 ? 'border-orange-200' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Stoc Scazut
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.low_stock_items?.length === 0 ? (
              <p className="text-slate-500 text-center py-4">✓ Toate articolele au stoc suficient</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {stats.low_stock_items?.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-xs text-slate-500">{item.code}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="border-orange-400 text-orange-600">
                        {item.current_stock} / {item.min_stock} {item.unit}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Critical Stock Alert */}
        <Card className={stats.critical_stock_items?.length > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Stoc Critic (Epuizat)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.critical_stock_items?.length === 0 ? (
              <p className="text-slate-500 text-center py-4">✓ Niciun articol epuizat</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {stats.critical_stock_items?.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-2 bg-red-100 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-xs text-slate-500">{item.code}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="destructive">
                        EPUIZAT - Min: {item.min_stock} {item.unit}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Activity Stats */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-sm text-slate-500">Miscari ultmele 7 zile</p>
                <p className="text-2xl font-bold">{stats.movements_last_7_days}</p>
              </div>
              <div className="h-8 w-px bg-slate-200" />
              <div>
                <p className="text-sm text-slate-500">Miscari ultmele 30 zile</p>
                <p className="text-2xl font-bold">{stats.movements_last_30_days}</p>
              </div>
              <div className="h-8 w-px bg-slate-200" />
              <div>
                <p className="text-sm text-slate-500">Total Locatii BIN</p>
                <p className="text-2xl font-bold">{stats.total_bins}</p>
              </div>
            </div>
            <Button variant="outline" onClick={fetchStats}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizeaza
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WMSDashboard;
